import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import axios from "axios";

// Constants
import {
  dateOfPublishing,
  spotlight,
  statusValue,
  trendingBlogs,
} from "../../components/Constants";

// Hooks
import useDebouncedSearch from "../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../components/Utils/hooks/useSortHandler";

// Components
import FilledButton from "../../components/Common/Buttons/FilledButton";
import Breadcrumbs from "../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import CommonFilterModal from "../../components/Common/CommonFilterModal";
import DoctorsFilterOptions from "../../components/Common/CommonFilterOptions";
import Pagination from "../../components/Common/CommonPagination/index";
import MainconternLayout from "../../components/Layout/MainconternLayout";
import NavBarTitles from "../../components/Common/TabBar";
import Deletepopup from "../../components/Modal/Deletepopup";
import HealthArticleTable from "./HealthArticleTable";

// Styles
import "./healtharticle.scss";

const emptyFilterValues = {
  Menu: [],
  Category: [],
  Status: [],
  "Date of Publishing": [],
  Trending: [],
  Spotlight: [],
};

const HealthArticle = () => {
  // Sorting
  const [sort, setSort] = useState("newest");
  const [sortName, setSortName] = useState("Sort by");
  const handleSortClick = useSortHandler(setSort, setSortName);

  // Search
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearchText, () => {
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
  });

  const [category, setCategory] = useState([]);
  const [menu, setMenu] = useState([]);
  // Filters
  const Filters = useMemo(
    () => ({
      Menu: menu,
      Category: category,
      Status: statusValue.map((data) => data.label),
      "Date of Publishing": dateOfPublishing,
      Trending: trendingBlogs,
      Spotlight: spotlight.map((data) => data.label),
    }),
    [menu, category],
  );
  const [filters, setFilters] = useState(Filters);
  const [selectedValues, setSelectedValues] = useState(emptyFilterValues);
  const [selectedCategory, setSelectedCategory] = useState("Menu");
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [tempSelectedValues, setTempSelectedValues] =
    useState(emptyFilterValues);

  // Delete
  const [del, setDel] = useState(false);
  const [id, setId] = useState("");

  const [loader, setLoader] = useState(true);

  // Pagination and Data
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}health-article/admin-blog-list`,
      initialParams: {
        sort,
        searchText,
        values: selectedValues,
      },
    });

  useEffect(() => {
    getCategory();
    getHealthArticle();
    (async () => {
      const menuResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}health-article-menu/getarray`,
      );
      const categoryResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}health-article-category/getarray`,
      );
      setFilters({
        ...filters,
        Menu: menuResponse.data,
        Category: categoryResponse.data,
      });
    })();
  }, []);

  useEffect(() => {
    setLoader(true);
    fetchData();
  }, [pagination.skip, pagination.limit, sort, searchText, selectedValues]);

  const getHealthArticle = () => {
    setLoader(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}health-article-menu/list`)
      .then((res) => {
        setMenu(res.data.rows.map((data) => data?.name));
      });
  };

  const getCategory = () => {
    setLoader(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}health-article-category/list`)
      .then((res) => {
        setCategory(res.data.rows.map((data) => data?.category));
      });
  };

  const handleActive = (id, status) => {
    setLoader(true);
    axios
      .patch(
        `${process.env.REACT_APP_API_URL}health-article/update/active/${id}`,
        {
          active: !status,
        },
      )
      .then(() => {
        fetchData();
      });
  };

  const handleDelete = useDeleteHandler("health-article", fetchData, setDel);

  const handlePageChange = usePageChange(pagination, setPagination);

  const navigate = useNavigate();

  const addNewHealthArticles = {
    id: "addnewpost",
    onClick: () => navigate("/healtharticleaddnew"),
    style: { width: "240px" },
    label: "Add New Health Articles",
  };

  const handleView = (id) => {
    navigate(`/healthArticleView/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/healtharticleEdit/${id}`);
  };

  const breadcrumbsList = useMemo(
    () => [{ text: "Health Articles", link: "" }],
    [],
  );

  const healthArticleNavData = [
    {
      name: "Health Articles",
      navigatePath: "/healtharticle",
    },
    {
      name: "Manage Menu & Category",
      navigatePath: "/healtharticlemenu" || "/Category",
    },
    {
      name: "HealthSy Stories",
      navigatePath: "/healthsystories",
    },
  ];

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);
  return (
    <MainconternLayout
      header={
        <div className="d-flex pb-2 flex-wrap align-items-center justify-content-between">
          <Breadcrumbs
            className="healthsyMasterBreadCrumbs"
            breadcrumbsList={breadcrumbsList}
          />
          <FilledButton props={addNewHealthArticles} />
        </div>
      }
      footer={
        <div className="mt-4 col d-flex align-items-center pagination justify-content-between admin-pagination">
          <div className="border border-danger bg-bg-light px-2 py-1 rounded">
            Total Health Articles : {totalCount}
          </div>
          {totalCount > pagination.limit && (
            <Pagination
              skip={pagination.skip}
              limit={pagination.limit}
              total={totalCount}
              onPageChange={(selected) => handlePageChange({ selected })}
            />
          )}
        </div>
      }
    >
      <div className="healtharticle-container healthsyContainer">
        <NavBarTitles titles={healthArticleNavData} />
        <DoctorsFilterOptions
          limit={pagination.limit}
          sortName={sortName}
          search={searchText}
          setFilterShow={() => setIsFilterModalVisible(true)}
          onChange={debouncedSearch}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          handleSortClick={handleSortClick}
        />
        <HealthArticleTable
          records={records}
          skip={pagination.skip}
          handleActive={handleActive}
          setId={setId}
          setDel={setDel}
          handleEdit={handleEdit}
          handleView={handleView}
          loader={loader}
        />
      </div>

      <CommonFilterModal
        visible={isFilterModalVisible}
        onClose={() => setIsFilterModalVisible(false)}
        onApply={async () => {
          await setSelectedValues(tempSelectedValues);
          await setIsFilterModalVisible(false);
          await setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
        }}
        onReset={() => {
          setSelectedValues(emptyFilterValues);
          setTempSelectedValues(emptyFilterValues);
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        filters={filters}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedValues={tempSelectedValues}
        setSelectedValues={setTempSelectedValues}
      />

      {del && (
        <Deletepopup
          handleDelete={handleDelete}
          id={id}
          handleClose={() => setDel(false)}
        />
      )}
    </MainconternLayout>
  );
};

export default HealthArticle;
